<script>
  import { themeCssVars } from "../lib/theme";

  export let items = [];
  export let selectedValues = [];
  export let placeholder = "Select...";
  export let getItemValue = (item) => item?.value;
  export let getItemLabel = (item) => item?.label ?? String(getItemValue(item) ?? "");
  export let getBadgeLabel = (item) => getItemLabel(item);
  export let otherOption = null;
  export let disabled = false;

  let container;
  let isOpen = false;

  $: if (disabled && isOpen) {
    isOpen = false;
  }

  const isSelected = (value) => selectedValues.includes(value);

  const close = () => {
    isOpen = false;
  };

  const toggleOpen = () => {
    if (disabled) return;
    isOpen = !isOpen;
  };

  const handleTriggerKeydown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleOpen();
    }
  };

  const handleWindowClick = (event) => {
    const target = event.target;
    if (isOpen && container && target && !container.contains(target)) {
      close();
    }
  };

  const handleItemChange = (value, checked) => {
    if (checked) {
      if (!selectedValues.includes(value)) {
        selectedValues = [...selectedValues, value];
      }
      return;
    }

    selectedValues = selectedValues.filter((selectedValue) => selectedValue !== value);
  };

  const handleOtherChange = (checked) => {
    if (!otherOption) return;

    if (checked) {
      selectedValues = otherOption.exclusive
        ? [otherOption.value]
        : [...selectedValues, otherOption.value];
      return;
    }

    selectedValues = selectedValues.filter((selectedValue) => selectedValue !== otherOption.value);
  };

  const getItemForValue = (value) => items.find((item) => getItemValue(item) === value);

  $: otherSelected = Boolean(otherOption && selectedValues.includes(otherOption.value));
</script>

<svelte:window on:click={handleWindowClick} />

<div class="relative" bind:this={container} style={themeCssVars}>
  <div class="relative">
    <div
      class="dropdown-trigger flex flex-wrap gap-2 min-h-[42px] pr-10 {disabled
        ? 'dropdown-trigger-disabled'
        : 'cursor-pointer'}"
      role="button"
      tabindex={disabled ? -1 : 0}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-disabled={disabled}
      on:click={toggleOpen}
      on:keydown={handleTriggerKeydown}
    >
      {#if selectedValues.length === 0}
        <span class="text-slate-400">{placeholder}</span>
      {/if}

      {#each selectedValues as selectedValue}
        {#if otherOption && selectedValue === otherOption.value}
          <span class="selected-chip">{otherOption.label}</span>
        {:else}
          {@const selectedItem = getItemForValue(selectedValue)}
          <span class="selected-chip">
            {selectedItem ? getBadgeLabel(selectedItem) : selectedValue}
          </span>
        {/if}
      {/each}
    </div>

    {#if isOpen}
      <button
        type="button"
        class="absolute inset-y-0 right-2 flex h-full items-center justify-center text-white/60 hover:text-white"
        on:click={close}
      >
        ✕
      </button>
    {/if}
  </div>

  {#if isOpen}
    <div class="dropdown-list absolute z-10 border rounded shadow mt-1 w-full max-h-60 overflow-y-auto">
      {#if !(otherOption?.exclusive && otherSelected)}
        {#each items as item (getItemValue(item))}
          <label class="dropdown-option flex items-center px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSelected(getItemValue(item))}
              on:change={(event) =>
                handleItemChange(getItemValue(item), event.currentTarget.checked)}
            />
            <span class="ml-2">{getItemLabel(item)}</span>
          </label>
        {/each}
      {/if}

      {#if otherOption}
        <label class="dropdown-option flex items-center px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected(otherOption.value)}
            on:change={(event) => handleOtherChange(event.currentTarget.checked)}
          />
          <span class="ml-2">{otherOption.label}</span>
        </label>
      {/if}
    </div>
  {/if}
</div>

<style>
  .dropdown-trigger {
    width: 100%;
    border: 1px solid var(--ace-border);
    border-radius: 0.5rem;
    padding: 0.5rem;
    background: var(--ace-surface);
    color: var(--ace-text);
  }

  .dropdown-trigger:focus-visible {
    outline: none;
    border-color: var(--ace-focus);
    box-shadow: 0 0 0 2px rgb(96 165 250 / 0.35);
  }

  .selected-chip {
    border-radius: 0.375rem;
    background: var(--ace-chip);
    color: #ffffff;
    display: inline-flex;
    max-width: 100%;
    overflow: hidden;
    padding: 0.25rem 0.5rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
  }

  .dropdown-list {
    background: var(--ace-dropdown);
    border-color: var(--ace-border);
    color: var(--ace-text);
  }

  .dropdown-option:hover {
    background: var(--ace-item-hover);
  }

  .dropdown-option input {
    accent-color: var(--ace-primary);
  }

  .dropdown-trigger-disabled {
    background: var(--ace-surface-muted);
    color: var(--ace-disabled-text);
    cursor: not-allowed;
  }
</style>
